import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import LegalAgreementView, { recordAgreement, toastAgreementError } from '../components/legal/LegalAgreementView';
import {
  clearHostOnboardingResult,
  clearHostListingDraft,
  loadHostListingDraft,
  saveHostOnboardingResult,
} from '../utils/hostOnboarding';
import { clearPendingHostListingInviteId, getPendingHostListingInviteData } from '../utils/deepLinkHandler';
import { trackInviteAccepted } from '../utils/airbridge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Final step of the host listing flow. ``HostVerifyListingPage`` writes the
 * validated listing URL + iCal URL + (optional) prefilled invite payload
 * into ``sessionStorage.hostListingDraft`` and navigates here. The actual
 * ``/host/apply`` submission happens AFTER the host ticks "I agree" so the
 * agreement record is created atomically with the listing.
 */
export default function HostListingAgreementPage() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    const stored = loadHostListingDraft();
    if (!stored || !stored.listing_url || !stored.ical_url) {
      toast.error('We lost your listing draft. Please re-enter it.');
      navigate('/host-verify-listing', { replace: true });
      return;
    }
    setDraft(stored);
  }, [navigate]);

  const handleAccept = async (version, extras = {}) => {
    if (!draft) return;
    setBusy(true);
    try {
      clearHostOnboardingResult();
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      const email = userStr ? JSON.parse(userStr).email : localStorage.getItem('pendingVerificationEmail');
      const payload = {
        listing_url: draft.listing_url,
        ical_url: draft.ical_url,
      };

      let response;
      if (token) {
        response = await axios.post(`${API}/host/apply`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else if (email) {
        response = await axios.post(`${API}/host/apply-public`, { ...payload, email });
      } else {
        toast.error('Session expired. Please sign in again.');
        navigate('/login', { replace: true });
        return;
      }

      const resultPayload = {
        outcome: response?.data?.outcome || 'reviewing',
        preview: response?.data?.preview || draft.preview || null,
        listing_url: draft.listing_url,
        ical_url: draft.ical_url,
      };

      // Airbridge invite attribution preserved from the original page.
      const prefilledInvite = getPendingHostListingInviteData();
      if (prefilledInvite?.invite_id) {
        trackInviteAccepted({
          inviteId: prefilledInvite.invite_id,
          inviteType: 'host_to_host',
          extra: {
            listing_url: draft.listing_url,
            outcome: resultPayload.outcome,
            variant: prefilledInvite.variant || 'a',
          },
        });
      }

      // Record the accepted agreement against this listing. We use the
      // listing URL as a stable entity_id since the public route does not
      // always return the freshly minted application id.
      try {
        await recordAgreement({
          kind: 'host',
          version,
          entity_id: response?.data?.application_id || draft.listing_url || '',
          entity_name: resultPayload.preview?.title || draft.listing_url,
          region: resultPayload.preview?.location_label || '',
          accepted_agreement: extras.accepted_agreement !== false,
          accepted_english_comprehension: !!extras.accepted_english_comprehension,
        });
      } catch (err) {
        toastAgreementError(err);
      }

      clearPendingHostListingInviteId();
      clearHostListingDraft();
      saveHostOnboardingResult(resultPayload);
      navigate('/host-pending', { replace: true, state: resultPayload });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to submit';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <LegalAgreementView
      kind="host"
      actionLabel="Agree & submit listing"
      onAccept={handleAccept}
      onBack={() => navigate('/host-verify-listing')}
      busy={busy}
      testIdPrefix="host-agreement"
    />
  );
}
